<template>
  <ErrorBoundary>
    <div class="app">
      <AppHeader />
      <MaintenanceBanner />
      <TermsPrompt />
      <main class="main-content" :class="{ 
        'header-expanded': headerExpanded,
        'no-date-selectors': !showDateSelectors,
        'with-footer': true
      }">
        <router-view v-slot="{ Component, route }">
          <transition name="page" mode="out-in">
            <div v-if="Component" :key="route.path" class="route-wrapper">
              <component :is="Component" />
            </div>
          </transition>
        </router-view>
      </main>
      <AppFooter />
      <ChatLauncher v-if="featuresStore.isAIAssistantEnabled" />
      <AmbientMusicPlayer />
    </div>
  </ErrorBoundary>
</template>

<script setup lang="ts">
import AppHeader from './components/AppHeader.vue'
import AppFooter from './components/AppFooter.vue'
import MaintenanceBanner from './components/MaintenanceBanner.vue'
import TermsPrompt from './components/TermsPrompt.vue'
import ErrorBoundary from './components/ErrorBoundary.vue'
import ChatLauncher from './components/ChatLauncher.vue'
import AmbientMusicPlayer from './components/AmbientMusicPlayer.vue'
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useFeaturesStore } from './stores/features'
import { useThemeStore } from './stores/theme'
import 'animate.css'

const route = useRoute()
const featuresStore = useFeaturesStore()
const themeStore = useThemeStore()

// Track header expanded state
const headerExpanded = ref(false)

// Determine if current route should show date selectors
const showDateSelectors = computed(() => {
  const pagesWithDateRange = ['home', 'admin']
  return pagesWithDateRange.includes(route.name as string)
})

const handleHeaderToggle = (event: CustomEvent) => {
  headerExpanded.value = event.detail.expanded
}

onMounted(() => {
  document.addEventListener('headerToggle', handleHeaderToggle as EventListener)
  tryStartAmbientMusic()
  // Fetch feature settings from backend
  featuresStore.fetchFeatureSettingsFromBackend()
  // Initialize theme
  themeStore.loadFromLocalStorage()
})

onUnmounted(() => {
  document.removeEventListener('headerToggle', handleHeaderToggle as EventListener)
  stopAmbientMusic()
})

// Quiet techno ambient on entry; fades out on first user interaction
let ambientCtx: AudioContext | null = null
let ambientGain: GainNode | null = null
let kickInterval: ReturnType<typeof setInterval> | null = null
let hatInterval: ReturnType<typeof setInterval> | null = null
let bassOsc: OscillatorNode | null = null

const scheduleKick = () => {
  if (!ambientCtx || !ambientGain) return
  const osc = ambientCtx.createOscillator()
  const gain = ambientCtx.createGain()
  osc.type = 'sine'
  osc.frequency.setValueAtTime(120, ambientCtx.currentTime)
  osc.frequency.exponentialRampToValueAtTime(40, ambientCtx.currentTime + 0.08)
  gain.gain.setValueAtTime(0.001, ambientCtx.currentTime)
  gain.gain.exponentialRampToValueAtTime(0.12, ambientCtx.currentTime + 0.01)
  gain.gain.exponentialRampToValueAtTime(0.001, ambientCtx.currentTime + 0.16)
  osc.connect(gain)
  gain.connect(ambientGain)
  osc.start()
  osc.stop(ambientCtx.currentTime + 0.2)
}

const scheduleHat = () => {
  if (!ambientCtx || !ambientGain) return
  const buffer = ambientCtx.createBuffer(1, 2205, ambientCtx.sampleRate)
  const data = buffer.getChannelData(0)
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length)
  const src = ambientCtx.createBufferSource()
  src.buffer = buffer
  const gain = ambientCtx.createGain()
  gain.gain.value = 0.03
  src.connect(gain)
  gain.connect(ambientGain)
  src.start()
}

const tryStartAmbientMusic = () => {
  if (!featuresStore.isFunModeEnabled || !featuresStore.isMusicEnabled) return
  try {
    ambientCtx = new (window.AudioContext || (window as any).webkitAudioContext)()
    ambientGain = ambientCtx.createGain()
    ambientGain.gain.value = 0.05
    ambientGain.connect(ambientCtx.destination)
    kickInterval = setInterval(scheduleKick, 600)
    hatInterval = setInterval(scheduleHat, 300)
    bassOsc = ambientCtx.createOscillator()
    const bassGain = ambientCtx.createGain()
    bassOsc.type = 'sawtooth'
    bassOsc.frequency.value = 55
    bassGain.gain.value = 0.005
    bassOsc.connect(bassGain)
    bassGain.connect(ambientGain)
    bassOsc.start()

    const stopOnce = () => fadeOutAndStopAmbient()
    window.addEventListener('pointerdown', stopOnce, { once: true, passive: true })
    window.addEventListener('keydown', stopOnce, { once: true })
    window.addEventListener('touchstart', stopOnce, { once: true, passive: true })
  } catch (_e) {
    // ignore
  }
}

const fadeOutAndStopAmbient = () => {
  if (!ambientCtx || !ambientGain) { stopAmbientMusic(); return }
  ambientGain.gain.cancelScheduledValues(ambientCtx.currentTime)
  ambientGain.gain.exponentialRampToValueAtTime(0.0001, ambientCtx.currentTime + 0.8)
  setTimeout(stopAmbientMusic, 900)
}

const stopAmbientMusic = () => {
  if (kickInterval) { clearInterval(kickInterval); kickInterval = null }
  if (hatInterval) { clearInterval(hatInterval); hatInterval = null }
  try { bassOsc?.stop() } catch(_) {}
  bassOsc = null
  if (ambientCtx) { try { ambientCtx.close() } catch(_) {} ; ambientCtx = null }
  ambientGain = null
}
</script>

<style>
.app {
  width: 100%;
  /* CRITICAL: Never allow this container to scroll */
  height: auto !important;
  min-height: auto !important;
  max-height: none !important;
  overflow: visible !important;
  overflow-y: visible !important;
  overflow-x: visible !important;
  contain: none !important;
  scroll-behavior: auto;
  overscroll-behavior-y: none;
  position: relative;
  box-sizing: border-box;
}

.main-content {
  padding-top: 2rem; /* Match desktop header height (default) */
  width: 100%;
  transition: padding-top 0.3s ease;
  margin: 0 auto; /* center content */
  /* CRITICAL: Never allow this container to scroll */
  height: auto !important;
  min-height: auto !important;
  max-height: none !important;
  overflow: visible !important;
  overflow-y: visible !important;
  overflow-x: visible !important;
  contain: none !important;
  scroll-behavior: auto;
  overscroll-behavior-y: none;
  position: relative;
  z-index: 1;
  box-sizing: border-box;
}

.main-content.header-expanded {
  padding-top: 5.5rem; /* Match expanded header height */
}

.main-content.no-date-selectors {
  padding-top: 2rem; /* Consistent 2rem padding for routes without date pickers */
}



@media (max-width: 768px) {
  .main-content {
    padding-top: 5rem; /* Match mobile header height (72px + 40px) */
    padding-bottom: 3rem;
    width: 100%;
  }

  .main-content.header-expanded {
    padding-top: 7rem; /* Match expanded mobile header height */
  }
  
  .main-content.no-date-selectors {
    padding-top: 2rem; /* Keep 2rem padding on mobile for routes without date pickers */
  }
  
  .main-content.with-footer {
    padding-bottom: 10.5rem; /* account for tab bar + footer + safe area */
  }
}

@media (max-width: 480px) {

  .main-content {
    width: 100%;
    padding-top: 6rem;
  }

  .main-content.header-expanded {
    padding-top: 8rem; /* Match expanded mobile header height */
  }
  
  .main-content.no-date-selectors {
    padding-top: 2rem; /* Keep 2rem padding on small mobile for routes without date pickers */
  }
  
  .main-content.with-footer {
    padding-bottom: 11rem; /* slightly larger for smaller phones */
  }
}

/* Route wrapper - ensures single root element for transitions */
.route-wrapper {
  width: 100%;
  /* CRITICAL: Never allow this container to scroll */
  height: auto !important;
  min-height: auto !important;
  max-height: none !important;
  overflow: visible !important;
  overflow-y: visible !important;
  overflow-x: visible !important;
  contain: none !important;
  scroll-behavior: auto;
  overscroll-behavior-y: none;
  box-sizing: border-box;
}

/* Page transitions */
.page-enter-active,
.page-leave-active {
  transition: all 0.3s ease-out;
}

.page-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.page-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* Fade transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Slide transitions */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from {
  opacity: 0;
  transform: translateX(-20px);
}

.slide-leave-to {
  opacity: 0;
  transform: translateX(20px);
}
</style>
